import { useState, useEffect, useRef, useCallback } from "react";

/** 将分页相关字段规范为整数（避免接口返回 string 时 `page + 1` 变成拼接字符串，如 "2"+1 => "21"） */
function toPageInt(value: unknown, fallback: number): number {
  const n = Number(value);
  const t = Math.trunc(n);
  if (!Number.isFinite(t) || t < 1) return fallback;
  return t;
}

function toTotalCount(value: unknown): number {
  const n = Number(value);
  const t = Math.trunc(n);
  if (!Number.isFinite(t) || t < 0) return 0;
  return t;
}

interface UseLoadMoreOptions<T> {
  /** 获取数据的函数 */
  fetcher: (page: number) => Promise<{
    list: T[];
    total: number;
    page: number;
    limit: number;
  }>;
  /** 依赖项变化时重置列表 */
  deps?: unknown[];
  /** 每页数量，默认10 */
  pageSize?: number;
  /** 初始页码，默认1 */
  initialPage?: number;
}

interface UseLoadMoreReturn<T> {
  /** 数据列表 */
  list: T[];
  /** 总数 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 是否还有更多 */
  hasMore: boolean;
  /** 加载状态 */
  loading: boolean;
  /** 重新加载（重置分页） */
  reload: () => void;
  /** 加载更多 */
  loadMore: () => void;
  /** 滚动容器ref */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * 上拉滚动加载hook
 * 
 * @example
 * const { list, loading, hasMore, loadMore, reload } = useLoadMore({
 *   fetcher: async (page) => {
 *     const res = await fetchCarList({ page, limit: 10 });
 *     return { list: res.list, total: res.total, page: res.page, limit: res.limit };
 *   },
 *   deps: [activeBrand, activeCategory, sort],
 * });
 */
export function useLoadMore<T>({
  fetcher,
  deps = [],
  pageSize = 10,
  initialPage = 1,
}: UseLoadMoreOptions<T>): UseLoadMoreReturn<T> {
  const safeInitialPage = toPageInt(initialPage, 1);
  const [list, setList] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(safeInitialPage);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const loadingRef = useRef(false);
  const pageRef = useRef(safeInitialPage);
  const containerRef = useRef<HTMLDivElement>(null);
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const loadData = useCallback(async (isReset: boolean) => {
    if (loadingRef.current) return;

    const currentPage = isReset
      ? safeInitialPage
      : toPageInt(pageRef.current, safeInitialPage);
    loadingRef.current = true;
    setLoading(true);

    try {
      const result = await fetcher(currentPage);

      const limitNum = toPageInt(result.limit, pageSize);
      const totalNum = toTotalCount(result.total);
      const nextPage = currentPage + 1;

      if (isReset) {
        setList(result.list);
        pageRef.current = nextPage;
        setPage(nextPage);
      } else {
        setList(prev => [...prev, ...result.list]);
        pageRef.current = nextPage;
        setPage(nextPage);
      }

      setTotal(totalNum);
      setHasMore(
        result.list.length > 0 && currentPage * limitNum < totalNum,
      );
    } catch (error) {
      console.error("加载数据失败:", error);
      if (isReset) {
        setList([]);
        setTotal(0);
        setHasMore(false);
      }
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [fetcher, pageSize, safeInitialPage]);

  // 重新加载
  const reload = useCallback(() => {
    pageRef.current = safeInitialPage;
    setPage(safeInitialPage);
    loadData(true);
  }, [loadData, safeInitialPage]);

  // 加载更多
  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore) {
      loadData(false);
    }
  }, [loadData, hasMore]);

  // 依赖变化时重新加载
  useEffect(() => {
    pageRef.current = safeInitialPage;
    setPage(safeInitialPage);
    loadData(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // 监听滚动
  // 容器自身限高可滚动时监听容器；否则回退到 window（页面级滚动）
  useEffect(() => {
    const container = containerRef.current;

    const handleScroll = () => {
      if (container && container.scrollHeight > container.clientHeight + 1) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        // 距离底部50px时触发加载
        if (scrollTop + clientHeight >= scrollHeight - 50) {
          loadMore();
        }
        return;
      }

      // 回退：页面级滚动
      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        loadMore();
      }
    };

    if (container) container.addEventListener("scroll", handleScroll);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (container) container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [loadMore]);

  return {
    list,
    total,
    page,
    hasMore,
    loading,
    reload,
    loadMore,
    containerRef,
  };
}
